import { Fragment } from 'react';

import Card, { CardProps } from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';

import { BannerConfig } from '~/hooks/useInfoBanners';
import { SITE_LIST_PAGE_SIZE } from '~/scenes/Sites/utils/constants';
import { getHighestPriorityBanner } from '~/utils/hosting/helpers';

import CardPagination from '../CardPagination';
import CardTopBanner from '../CardTopBanner';

interface HostingResourceCardPaginationProps {
  resultCount: number;
  page: number;
  rowsPerPage: number;
  onChangePage: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => void;
  onChangeRowsPerPage: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
}

interface HostingResourceCardProps extends CardProps {
  children: React.ReactNode;
  banners?: BannerConfig[];
  pagination?: HostingResourceCardPaginationProps;
}

const HostingResourceCard = ({
  children,
  banners = [],
  sx = undefined,
  pagination = undefined,
  ...rest
}: HostingResourceCardProps) => {
  const theme = useTheme();
  const { borderRadius } = theme.shape;
  const prioritizedBanners = getHighestPriorityBanner(banners);
  const hasBanners = prioritizedBanners.length > 0;

  return (
    <Stack direction="column" spacing={0}>
      {hasBanners && (
        <Stack overflow="hidden">
          {prioritizedBanners
            .filter(({ hide }) => !hide)
            .map(({ key, render, ctbData, ...bannerProps }) =>
              render ? (
                <Fragment key={key}>{render}</Fragment>
              ) : (
                <CardTopBanner key={key} {...bannerProps} ctbData={ctbData} />
              ),
            )}
        </Stack>
      )}
      <Card
        variant="outlined"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderRadius: borderRadius,
          p: 0,
          ...sx,
        }}
        {...rest}
      >
        <Stack sx={{ p: 3 }}>{children}</Stack>

        {pagination && pagination.resultCount > SITE_LIST_PAGE_SIZE && (
          <CardPagination {...pagination} />
        )}
      </Card>
    </Stack>
  );
};

export default HostingResourceCard;
